import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { NavLink } from "@/components/NavLink";
import { Shield, Globe, Clock, Award, MapPin, Users, CheckCircle, Anchor, Ship, TrendingUp, Heart, Building, Target, Lightbulb, Handshake, Star, Package, Plane, Truck, FileCheck, BarChart3, Zap } from "lucide-react";
import warehouseImage from "@/assets/warehouse.jpg";
import teamWarehouse from "@/assets/team-warehouse.jpg";
import globalNetwork from "@/assets/global-network.jpg";
import heroPort from "@/assets/hero-port.jpg";
import operationsTeam from "@/assets/operations-team.jpg";
import airFreight from "@/assets/air-freight.jpg";
import projectCargo from "@/assets/project-cargo.jpg";
import bannerTerminal from "@/assets/banner-terminal.jpg";
import { useState } from "react";

const About = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const milestones = [
    { year: "1985", title: "Founded in California", desc: "Started as a shipping agency in California, USA." },
    { year: "1992", title: "First International Partnerships", desc: "Established routes to Europe and the Mediterranean." },
    { year: "1998", title: "Global Expansion", desc: "Expanded operations to the Asia-Pacific market." },
    { year: "2005", title: "Global Network", desc: "Established partnerships with 50+ international ports." },
    { year: "2010", title: "Warehousing Division", desc: "Opened first dedicated logistics center in California." },
    { year: "2015", title: "ASEAN Network", desc: "Expanded regional partnerships for complete ASEAN coverage." },
    { year: "2018", title: "Digital Transformation", desc: "Launched online tracking and booking platform." },
    { year: "2022", title: "ISO 45001 Certified", desc: "Achieved occupational health and safety certification." },
    { year: "2024", title: "Sustainability Commitment", desc: "Achieved carbon-neutral operations certification." }
  ];

  const values = [
    { icon: Shield, title: "Integrity", desc: "We operate with transparency and honesty in every interaction" },
    { icon: Target, title: "Excellence", desc: "We strive for the highest standards in service delivery" },
    { icon: Lightbulb, title: "Innovation", desc: "We embrace technology to improve logistics solutions" },
    { icon: Handshake, title: "Partnership", desc: "We build lasting relationships with clients and partners" },
    { icon: Heart, title: "Care", desc: "We care for our people, community, and environment" },
    { icon: Globe, title: "Global Mindset", desc: "We think globally while acting locally in USA" }
  ];

  const team = [
    { name: "Daniel Davis", role: "CEO & Founder", bio: "30+ years in maritime logistics. Former executive at major lines.", specialty: "Strategic Leadership" },
    { name: "Elena Smith", role: "Chief Operating Officer", bio: "Expert in supply chain optimization with MBA from INSEAD.", specialty: "Operations Excellence" },
    { name: "Nick Vance", role: "Director of Operations", bio: "20 years in container shipping. Led operations at major US ports.", specialty: "Port Operations" },
    { name: "Sean Tanaka", role: "Director of APAC Operations", bio: "15 years managing ASEAN logistics. Expert in regional trade.", specialty: "Asia-Pacific Operations" },
    { name: "Maria Connor", role: "Head of Commercial", bio: "15 years in freight forwarding. Expert in trade compliance.", specialty: "Business Development" },
    { name: "Andrew George", role: "Technology Director", bio: "Former CTO at logistics startup. Digital transformation expert.", specialty: "Digital Innovation" },
    { name: "Pearl Charoen", role: "Head of International Relations", bio: "18 years in global shipping industry. Expert in trade agreements.", specialty: "Global Trade" },
    { name: "Sophia Davis", role: "HR Director", bio: "Organizational development specialist. 12 years in shipping HR.", specialty: "People & Culture" }
  ];

  const achievements = [
    { value: "40+", label: "Years of Experience", icon: Award },
    { value: "120+", label: "Partner Ports", icon: Globe },
    { value: "50,000+", label: "Annual Shipments", icon: Ship },
    { value: "2,500+", label: "Active Clients", icon: Users },
    { value: "99.8%", label: "On-Time Delivery", icon: Clock },
    { value: "250+", label: "Employees", icon: Building },
    { value: "50+", label: "Countries Served", icon: MapPin },
    { value: "50,000m²", label: "Warehouse Space", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src={heroPort}
          alt="Costal Meridian Port Operations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <Anchor className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Costal Meridian</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Headquartered in Costa Mesa, California, USA, delivering world-class shipping solutions with global reach since 1985.
          </p>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Story Section */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Our Story</h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Founded in California in 1985, Costal Meridian has grown from a shipping agent to a trusted global logistics partner. Our roots in maritime operations give us unique insights into international trade routes and supply chain excellence.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Over the years, we have expanded our services to serve the Americas, European, and Asian markets. Today, with our corporate headquarters in California, USA, we serve as a true global bridge, offering unparalleled coverage across the globe.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Our 120+ partner ports worldwide handle everything from standard container shipments to complex project cargo. Our headquarters provides 24/7 operations support across time zones.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  What sets us apart is our commitment to operational excellence, transparent communication, and client-first solutions. Every shipment receives the same attention to detail.
                </p>
                <Button asChild className="hover-scale">
                  <NavLink to="/contact">Connect With Us</NavLink>
                </Button>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src={warehouseImage}
                  alt="Costal Meridian warehouse facility"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Key Achievements */}
          <section className="mb-20 py-16 bg-gradient-to-r from-maritime-dark to-maritime-light rounded-xl text-primary-foreground animate-fade-in">
            <div className="container mx-auto px-8">
              <h2 className="text-3xl font-bold text-center mb-12">Costal Meridian at a Glance</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {achievements.map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <div key={idx} className="text-center">
                      <Icon className="h-10 w-10 mx-auto mb-3 text-accent" />
                      <div className="text-3xl md:text-4xl font-bold mb-1">{stat.value}</div>
                      <p className="text-sm opacity-80">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="mb-20 bg-secondary rounded-xl p-12 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="text-center md:text-left">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-muted-foreground text-lg">
                  To provide seamless, reliable, and innovative logistics solutions that empower businesses to thrive in global trade. From our US corporate headquarters, we connect markets, optimize supply chains, and deliver excellence in every shipment.
                </p>
              </div>
              <div className="text-center md:text-left">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4">
                  <Lightbulb className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-muted-foreground text-lg">
                  To be the most trusted and innovative logistics partner bridging markets worldwide, setting the standard for sustainable shipping practices while expanding our global footprint.
                </p>
              </div>
            </div>
          </section>

          {/* Headquarters Section */}
          <section className="mb-20 animate-fade-in">
            <h2 className="text-4xl font-bold text-center mb-12">Our Corporate Headquarters</h2>
            <div className="max-w-xl mx-auto">
              <Card className="hover-scale overflow-hidden">
                <div className="h-64 overflow-hidden">
                  <img src={teamWarehouse} alt="Costa Mesa Headquarters" className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Costa Mesa, California, USA</h3>
                      <p className="text-sm text-muted-foreground">Global Headquarters</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our corporate headquarters in Orange County, CA. Leading international trade route coordination, supply chain engineering, and administrative functions for our worldwide network.
                  </p>
                  <ul className="space-y-1 text-xs">
                    <li className="flex items-center"><CheckCircle className="h-3 w-3 text-primary mr-2" />Global administration</li>
                    <li className="flex items-center"><CheckCircle className="h-3 w-3 text-primary mr-2" />Transpacific trade expertise</li>
                    <li className="flex items-center"><CheckCircle className="h-3 w-3 text-primary mr-2" />24/7 coordination team</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Company Timeline */}
          <section className="mb-20 animate-fade-in">
            <h2 className="text-4xl font-bold text-center mb-12">Our Journey</h2>
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary/20 hidden md:block" />
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <Card className="inline-block hover-scale">
                        <CardContent className="p-6">
                          <div className="text-2xl font-bold text-primary mb-2">{milestone.year}</div>
                          <h3 className="text-xl font-semibold mb-2">{milestone.title}</h3>
                          <p className="text-muted-foreground">{milestone.desc}</p>
                        </CardContent>
                      </Card>
                    </div>
                    <div className="w-4 h-4 bg-primary rounded-full hidden md:block z-10" />
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-20 animate-fade-in">
            <h2 className="text-4xl font-bold text-center mb-12">Our Core Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, idx) => {
                const Icon = value.icon;
                return (
                  <Card key={idx} className="border-2 hover:border-primary transition-all hover-scale">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                      <p className="text-muted-foreground">{value.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img
                  src={globalNetwork}
                  alt="Costal Meridian Global Network"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-4xl font-bold mb-6">Why Choose Costal Meridian?</h2>
                <div className="space-y-6">
                  {[
                    { icon: Shield, title: "Global Advantage", desc: "Our California headquarters and partner network provide 24/7 coverage across time zones and regional expertise in Europe, Asia-Pacific, and the Americas." },
                    { icon: Globe, title: "Global Network", desc: "Partner ports in 120+ locations worldwide. Local expertise, global reach." },
                    { icon: Clock, title: "24/7 Support", desc: "Our team is available around the clock for your peace of mind." },
                    { icon: TrendingUp, title: "Cost Optimization", desc: "Strategic routing and consolidation to minimize costs without compromising service." }
                  ].map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div key={idx} className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                          <p className="text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* Our Services Overview */}
          <section className="mb-20 bg-secondary rounded-lg p-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Our Core Services</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Ship, title: "Ocean Freight", desc: "FCL & LCL worldwide" },
                { icon: Plane, title: "Air Freight", desc: "Express global delivery" },
                { icon: Truck, title: "Intermodal", desc: "Multi-mode transport" },
                { icon: Package, title: "Warehousing", desc: "50,000m² facilities" },
                { icon: FileCheck, title: "Customs", desc: "Expert brokerage" },
                { icon: Shield, title: "Insurance", desc: "Cargo protection" },
                { icon: BarChart3, title: "Supply Chain", desc: "Strategic consulting" },
                { icon: Zap, title: "Project Cargo", desc: "Heavy lift expertise" },
              ].map((service, idx) => {
                const Icon = service.icon;
                return (
                  <div key={idx} className="text-center p-4 bg-background rounded-lg hover:shadow-lg transition-all hover-scale">
                    <Icon className="h-10 w-10 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold mb-1">{service.title}</h3>
                    <p className="text-xs text-muted-foreground">{service.desc}</p>
                  </div>
                );
              })}
            </div>
            <div className="text-center mt-8">
              <Button asChild className="hover-scale">
                <NavLink to="/services">View All Services</NavLink>
              </Button>
            </div>
          </section>

          {/* Certifications */}
          <section className="bg-secondary rounded-lg p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Accreditations & Certifications</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { icon: Award, title: "ISO 9001:2015", desc: "Quality Management" },
                { icon: Shield, title: "ISO 14001", desc: "Environmental" },
                { icon: CheckCircle, title: "ISO 45001", desc: "Safety Standards" },
                { icon: Globe, title: "AEO Certified", desc: "Authorized Economic Operator" },
                { icon: Ship, title: "FIATA Member", desc: "Freight Forwarders" },
                { icon: Building, title: "IATA Accredited", desc: "Air Freight" },
                { icon: Anchor, title: "FMC Licensed", desc: "Federal Maritime Commission" },
                { icon: TrendingUp, title: "WCA Network", desc: "Global Partner" }
              ].map((cert, idx) => {
                const Icon = cert.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="font-semibold">{cert.title}</h3>
                    <p className="text-sm text-muted-foreground">{cert.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Team Section */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-4">Our Leadership Team</h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              Meet the experienced professionals driving Costal Meridian's success.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, idx) => (
                <Card key={idx} className="hover-scale">
                  <CardContent className="p-6 text-center">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-10 w-10 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold mb-1">{member.name}</h3>
                    <p className="text-primary font-semibold text-sm mb-2">{member.role}</p>
                    <p className="text-xs text-muted-foreground mb-3">{member.bio}</p>
                    <span className="inline-block bg-secondary text-xs px-3 py-1 rounded-full">{member.specialty}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Operations Team Image */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src={airFreight}
                  alt="Costal Meridian Air Freight Operations"
                  className="w-full h-[300px] object-cover"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src={projectCargo}
                  alt="Costal Meridian Project Cargo Operations"
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>
            <div className="text-center mt-8">
              <h3 className="text-2xl font-bold mb-4">Join Our Team</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                We're always looking for talented individuals to join our growing team. Explore career opportunities at Costal Meridian.
              </p>
              <Button variant="outline" asChild className="hover-scale">
                <NavLink to="/contact">View Opportunities</NavLink>
              </Button>
            </div>
          </section>

          {/* Global Presence */}
          <section className="mb-20 bg-secondary rounded-lg p-12 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Global Presence</h2>
            <div className="mb-12 rounded-lg overflow-hidden shadow-xl">
              <img src={teamWarehouse} alt="Costal Meridian Operations Team" className="w-full h-[400px] object-cover" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: "120+", label: "Partner Ports" },
                { value: "50,000m²", label: "Warehouse Space" },
                { value: "50,000+", label: "Shipments/Year" },
                { value: "99.8%", label: "On-Time Delivery" },
                { value: "250+", label: "Employees" },
                { value: "40+", label: "Years Experience" },
                { value: "50+", label: "Countries Served" },
                { value: "24/7", label: "Support Available" }
              ].map((stat, idx) => (
                <div key={idx}>
                  <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <p className="text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Sustainability */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Sustainability & CSR</h2>
            <div className="max-w-5xl mx-auto">
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Operating globally, we understand our responsibility to preserve global environments while supporting our local communities.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="hover-scale">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Globe className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Environmental Initiatives</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li>• Carbon offset programs for all shipments</li>
                      <li>• Electric vehicles in our logistics operations</li>
                      <li>• Paperless documentation systems</li>
                      <li>• Energy-efficient warehouse operations</li>
                      <li>• Solar-powered logistics centers</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="hover-scale">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <Heart className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Community Support</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li>• Local employment priority in all locations</li>
                      <li>• Maritime education sponsorships</li>
                      <li>• Port community partnerships</li>
                      <li>• Charitable logistics support</li>
                      <li>• Youth internship programs</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="hover-scale">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                      <Award className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Recognition</h3>
                    <ul className="space-y-2 text-muted-foreground text-sm">
                      <li>• Green Shipping Award 2024</li>
                      <li>• Best Employer in Logistics 2023</li>
                      <li>• ISO 14001 Certified</li>
                      <li>• UN Global Compact Member</li>
                      <li>• Carbon Neutral Operations</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Office Locations */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Our Headquarters</h2>
            <div className="max-w-xl mx-auto">
              <Card className="max-w-full">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-3 mb-4">
                    <MapPin className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <h3 className="font-bold text-lg mb-2">USA Corporate Headquarters</h3>
                      <p className="text-sm text-muted-foreground">
                        555 Anton Blvd suite 1050<br />
                        Costa Mesa, CA 92626, USA
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 mb-4">
                    <Clock className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Mon - Fri: 8:00 - 18:00 PST
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-primary mt-1" />
                    <div>
                      <p className="text-sm text-muted-foreground">
                        (714) 798-3650<br />
                        support@costalmeridian.com
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Ready to Partner With Us?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Experience the Costal Meridian difference. Let our expertise work for your supply chain.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Request a Quote
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Our Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default About;